import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash2, KeyRound, ShieldCheck, HardHat, Headset, User, Pencil } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const ROLE_META = {
  super_admin:   { label: "Super Admin",    icon: ShieldCheck, tint: "text-amber-500 bg-amber-500/10" },
  company_admin: { label: "Company Admin",  icon: ShieldCheck, tint: "text-purple-500 bg-purple-500/10" },
  dispatcher:    { label: "Dispatcher",     icon: Headset,     tint: "text-blue-500 bg-blue-500/10" },
  engineer:      { label: "Engineer",       icon: HardHat,     tint: "text-emerald-500 bg-emerald-500/10" },
  customer:      { label: "Customer",       icon: User,        tint: "text-slate-500 bg-slate-500/10" },
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? users : users.filter((u) => u.role === filter)),
    [users, filter]
  );

  const counts = useMemo(() => {
    const c = { all: users.length };
    for (const u of users) c[u.role] = (c[u.role] || 0) + 1;
    return c;
  }, [users]);

  const canManage = me?.role === "super_admin" || me?.role === "company_admin";
  const isSuper = me?.role === "super_admin";

  const selectableIds = useMemo(
    () => filtered.filter((u) => u.id !== me?.id && (u.role !== "super_admin" || isSuper)).map((u) => u.id),
    [filtered, me, isSuper]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) selectableIds.forEach((id) => n.delete(id));
    else selectableIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());

  const removeUser = async (u) => {
    if (u.id === me.id) return alert("You cannot delete your own account.");
    if (!window.confirm(`Delete user "${u.name}" (${u.email})?`)) return;
    try { await api.delete(`/users/${u.id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} user(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(ids, (id) => api.delete(`/users/${id}`));
    setBulkBusy(false);
    clearSel();
    await load();
    if (ok < ids.length) setErr(`${ids.length - ok} deletion(s) failed.`);
  };

  const filters = [
    ["all", "All"], ["super_admin", "Super Admin"], ["company_admin", "Company Admin"],
    ["dispatcher", "Dispatcher"], ["engineer", "Engineer"], ["customer", "Customer"],
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Access control</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Users & Permissions</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create, disable, and reset passwords for every role.</p>
        </div>
        {canManage && (
          <button data-testid="user-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New user
          </button>
        )}
      </header>

      {err && <div data-testid="users-error" className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map(([key, label]) => (
          <button key={key} data-testid={`user-filter-${key}`} onClick={() => setFilter(key)}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${filter === key ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
            {label}
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] tabular-nums text-foreground/70">{counts[key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="sharp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 px-4 py-2.5">
                  <RowCheckbox testid="user-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5">Phone</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.customer;
                const Icon = meta.icon;
                const isSelf = u.id === me?.id;
                const canTouch = canManage && (u.role !== "super_admin" || isSuper);
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/40 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-3">
                      <RowCheckbox testid={`user-select-${u.id}`} checked={selected.has(u.id)}
                        onChange={() => toggleOne(u.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${meta.tint}`}><Icon className="h-4 w-4" /></div>
                        <span className="font-medium">{u.name}</span>
                        {isSelf && <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">you</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${meta.tint}`}>{meta.label}</span></td>
                    <td className="px-4 py-3 text-muted-foreground">{u.phone || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] ${u.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-500"}`}>{u.status || "active"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button data-testid={`user-edit-${u.id}`} disabled={!canTouch} onClick={() => setEditing(u)}
                          className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button data-testid={`user-reset-${u.id}`} disabled={!canTouch} onClick={() => setResetting(u)}
                          className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed">
                          <KeyRound className="h-3.5 w-3.5" /> Reset password
                        </button>
                        <button data-testid={`user-delete-${u.id}`} disabled={!canTouch || isSelf} onClick={() => removeUser(u)}
                          className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">{busy ? "Loading…" : "No users match this filter."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BulkBar count={selected.size} entityLabel="users" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <CreateUserModal isSuper={isSuper} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <EditUserModal user={editing} isSuper={isSuper} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, isSuper }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role, status: user.status || "active" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.patch(`/users/${user.id}`, form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const roleOptions = [
    ["dispatcher", "Dispatcher"], ["engineer", "Engineer"],
    ["company_admin", "Company Admin"], ["customer", "Customer"],
    ...(isSuper ? [["super_admin", "Super Admin"]] : []),
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Edit user — {user.name}</h3>
          <button data-testid="user-edit-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div><label className="label-eyebrow mb-1.5 block">Full name</label>
            <input data-testid="user-edit-name" value={form.name} onChange={set("name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Email</label>
            <input data-testid="user-edit-email" type="email" value={form.email} onChange={set("email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Phone</label>
            <input data-testid="user-edit-phone" value={form.phone} onChange={set("phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Role</label>
            <select data-testid="user-edit-role" value={form.role} onChange={set("role")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><label className="label-eyebrow mb-1.5 block">Status</label>
            <select data-testid="user-edit-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </select></div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="user-edit-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onSaved, isSuper }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "dispatcher" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/users", form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const roleOptions = [
    ["dispatcher", "Dispatcher"], ["engineer", "Engineer"],
    ["company_admin", "Company Admin"], ["customer", "Customer"],
    ...(isSuper ? [["super_admin", "Super Admin"]] : []),
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">New user account</h3>
          <button data-testid="user-create-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div><label className="label-eyebrow mb-1.5 block">Full name *</label>
            <input data-testid="user-create-name" required value={form.name} onChange={set("name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Email *</label>
            <input data-testid="user-create-email" required type="email" value={form.email} onChange={set("email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Phone</label>
            <input data-testid="user-create-phone" value={form.phone} onChange={set("phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Role *</label>
            <select data-testid="user-create-role" value={form.role} onChange={set("role")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><label className="label-eyebrow mb-1.5 block">Temporary password * (min 8 chars)</label>
            <input data-testid="user-create-password" required type="password" minLength={8} value={form.password} onChange={set("password")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="user-create-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">{busy ? "Creating…" : "Create user"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [ok, setOk] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setOk(false);
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords do not match.");
    setBusy(true);
    try { await api.post(`/users/${user.id}/reset-password`, { new_password: pw }); setOk(true); setTimeout(onClose, 1200); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Reset password — {user.name}</h3>
          <button data-testid="user-reset-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <p className="text-xs text-muted-foreground">Setting password for <span className="font-medium text-foreground">{user.email}</span></p>
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 px-2.5 py-2 text-[11px] leading-relaxed text-blue-600 dark:text-blue-400">
            Any existing lockout on this account is cleared automatically once the new password is saved — the user can log in immediately with the new password.
          </div>
          <div><label className="label-eyebrow mb-1.5 block">New password (min 8 chars)</label>
            <input data-testid="user-reset-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Confirm new password</label>
            <input data-testid="user-reset-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          {ok && <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">Password updated.</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="user-reset-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Update password"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
